import { Component, OnInit, OnDestroy, inject, signal, computed } from '@angular/core';
import { CommonModule } from '@angular/common';
import { Router, ActivatedRoute, RouterLink } from '@angular/router';
import { Subject, takeUntil } from 'rxjs';
import { AgremoAnalysisService } from '../../core/data-services/agremo-analysis.service';
import { AgremoAnalysis } from '../../core/data-models/agremo-analysis.model';
import { AgremoAnalysisFile } from '../../core/response-models/agremo-analysis-file.model';
import { AgremoAnalysisStatus } from '../../core/data-models/agremo-analysis-status.model';
import { AgremoProgressEvent } from '../../core/response-models/agremo-progress-event.model';
import { AgremoCompleteEvent } from '../../core/response-models/agremo-complete-event.model';
import { AgremoFailedEvent } from '../../core/response-models/agremo-failed-event.model';
import { WebSocketService } from '../../core/services/websocket.service';
import { DateFormatPipe } from '../../shared/pipes';
import { AgremoStatisticsComponent } from '../../shared/components/agremo-statistics/agremo-statistics.component';
import { openExternal } from '../../core/utils/external-link-utils';

/**
 * Analysis Detail Component
 *
 * Displays analysis metadata, progress, result files, and field boundary thumbnail.
 * Subscribes to WebSocket for real-time progress updates.
 *
 * Route: /analyses/:id/view
 */
@Component({
  selector: 'app-analysis-detail',
  standalone: true,
  imports: [CommonModule, RouterLink, DateFormatPipe, AgremoStatisticsComponent],
  templateUrl: './analysis-detail.component.html',
  styleUrls: ['./analysis-detail.component.scss']
})
export class AnalysisDetailComponent implements OnInit, OnDestroy {
  private router = inject(Router);
  private route = inject(ActivatedRoute);
  private agremoAnalysisService = inject(AgremoAnalysisService);
  private webSocketService = inject(WebSocketService);
  private destroy$ = new Subject<void>();

  // State
  analysisId: string | null = null;
  analysis = signal<AgremoAnalysis | null>(null);
  isLoading = signal<boolean>(true);
  errorMessage = signal<string | null>(null);

  // Computed
  readonly status = computed(() => this.analysis()?.status ?? 'pending');
  readonly progress = computed(() => this.analysis()?.progress ?? 0);
  readonly isProcessing = computed(() => {
    const s = this.status();
    return s === 'processing' || s === 'queued' || s === 'pending' || s === 'downloading';
  });
  readonly isCompleted = computed(() => this.status() === 'completed');
  readonly isFailed = computed(() => this.status() === 'failed');

  // Boundary computed
  readonly hasBoundary = computed(() => !!this.analysis()?.boundaryGeojson);

  // File groupings
  readonly pdfFiles = computed(() =>
    (this.analysis()?.files ?? []).filter(f => f.fileType === 'pdf')
  );

  readonly rawDataFiles = computed(() =>
    (this.analysis()?.files ?? []).filter(f =>
      f.fileType === 'tiff' || f.fileType === 'shp' || f.fileType === 'png'
    )
  );

  readonly hasFiles = computed(() =>
    this.pdfFiles().length > 0 || this.rawDataFiles().length > 0
  );

  readonly hasStatistics = computed(() => {
    const stats = this.analysis()?.statistics;
    return !!stats && Object.keys(stats).length > 0;
  });

  ngOnInit(): void {
    this.analysisId = this.route.snapshot.paramMap.get('id');
    if (this.analysisId) {
      this.loadAnalysis(this.analysisId);
      this.setupWebSocketListeners();
    } else {
      this.errorMessage.set('No analysis ID provided');
      this.isLoading.set(false);
    }
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  private async loadAnalysis(id: string): Promise<void> {
    this.isLoading.set(true);
    this.errorMessage.set(null);

    try {
      const data = await this.agremoAnalysisService.getAnalysisById(id);
      if (data) {
        this.analysis.set(data);
      } else {
        this.errorMessage.set('Analysis not found');
      }
    } catch (error: any) {
      console.error('Error loading analysis:', error);
      this.errorMessage.set(error.message || 'Failed to load analysis');
    } finally {
      this.isLoading.set(false);
    }
  }

  /**
   * Set up WebSocket listeners for real-time updates specific to this analysis
   */
  private setupWebSocketListeners(): void {
    // Progress updates
    this.webSocketService.on<AgremoProgressEvent>('agremo-analysis:progress')
      .pipe(takeUntil(this.destroy$))
      .subscribe(event => {
        if (event.analysisId === this.analysisId) {
          this.analysis.update(a => a ? {
            ...a,
            status: event.status,
            progress: event.progress
          } : a);
        }
      });

    // Complete event - reload full data to get files
    this.webSocketService.on<AgremoCompleteEvent>('agremo-analysis:complete')
      .pipe(takeUntil(this.destroy$))
      .subscribe(event => {
        if (event.analysisId === this.analysisId) {
          // Reload to get file data
          this.loadAnalysis(this.analysisId!);
        }
      });

    // Failed event
    this.webSocketService.on<AgremoFailedEvent>('agremo-analysis:failed')
      .pipe(takeUntil(this.destroy$))
      .subscribe(event => {
        if (event.analysisId === this.analysisId) {
          this.analysis.update(a => a ? {
            ...a,
            status: 'failed',
            errorMessage: event.error,
            progress: 0
          } : a);
        }
      });
  }

  // Navigation
  goBack(): void {
    this.router.navigate(['/analyses']);
  }

  // Status helpers
  getStatusText(status: AgremoAnalysisStatus): string {
    return this.agremoAnalysisService.getStatusText(status);
  }

  getStatusBadgeClass(status: AgremoAnalysisStatus): string {
    return this.agremoAnalysisService.getStatusBadgeClass(status);
  }

  getStatusIcon(status: AgremoAnalysisStatus): string {
    return this.agremoAnalysisService.getStatusIcon(status);
  }

  // File helpers
  getFileTypeLabel(fileType: string): string {
    return this.agremoAnalysisService.getFileTypeLabel(fileType);
  }

  getFileTypeIcon(fileType: string): string {
    return this.agremoAnalysisService.getFileTypeIcon(fileType);
  }

  downloadFile(file: AgremoAnalysisFile): void {
    if (file.downloadUrl) {
      openExternal(file.downloadUrl);
    }
  }

  viewReport(file: AgremoAnalysisFile): void {
    if (file.downloadUrl) {
      openExternal(file.downloadUrl);
    }
  }

  // ============================================
  // BOUNDARY SVG METHODS
  // ============================================

  /**
   * Convert GeoJSON polygon coordinates to an SVG path string.
   * Uses a simple coordinate-to-viewport transform to normalize lat/lng
   * to fit within a 200x150 viewBox with padding.
   * Flips Y axis since SVG Y goes down but latitude goes up.
   */
  getBoundarySvgPath(geojson: any): string {
    if (!geojson) return '';

    try {
      // Extract coordinates from GeoJSON - handle both Geometry and Feature
      let coordinates: number[][];
      if (geojson.type === 'Feature' && geojson.geometry) {
        coordinates = geojson.geometry.coordinates?.[0];
      } else if (geojson.type === 'Polygon') {
        coordinates = geojson.coordinates?.[0];
      } else if (geojson.type === 'MultiPolygon') {
        coordinates = geojson.coordinates?.[0]?.[0];
      } else {
        return '';
      }

      if (!coordinates || coordinates.length < 3) return '';

      // Find bounds (single-pass to avoid stack overflow on large coordinate arrays)
      let minLng = Infinity, maxLng = -Infinity, minLat = Infinity, maxLat = -Infinity;
      for (const c of coordinates) {
        if (c[0] < minLng) minLng = c[0];
        if (c[0] > maxLng) maxLng = c[0];
        if (c[1] < minLat) minLat = c[1];
        if (c[1] > maxLat) maxLat = c[1];
      }

      const lngRange = maxLng - minLng || 1;
      const latRange = maxLat - minLat || 1;

      // Padding
      const padding = 10;
      const drawWidth = 200 - 2 * padding;
      const drawHeight = 150 - 2 * padding;

      // Scale to fit viewBox with aspect ratio preservation
      const scale = Math.min(drawWidth / lngRange, drawHeight / latRange);
      const offsetX = padding + (drawWidth - lngRange * scale) / 2;
      const offsetY = padding + (drawHeight - latRange * scale) / 2;

      // Build SVG path - flip Y axis
      const points = coordinates.map(coord => {
        const x = offsetX + (coord[0] - minLng) * scale;
        const y = offsetY + (maxLat - coord[1]) * scale;
        return `${x.toFixed(1)},${y.toFixed(1)}`;
      });

      return `M ${points.join(' L ')} Z`;
    } catch {
      return '';
    }
  }
}
